import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  CircularProgress,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Business,
  Phone,
  Email,
} from '@mui/icons-material';
import { clientsAPI } from '../services/api';

const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0,2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0,2)}-${digits.slice(2,5)}-${digits.slice(5)}`;
    return `${digits.slice(0,2)}-${digits.slice(2,6)}-${digits.slice(6,10)}`;
  }
  if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7,11)}`;
};

function ClientList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);

  const [formData, setFormData] = useState({
    company_name: '',
    business_number: '',
    address: '',
    manager_name: '',
    manager_phone: '',
    manager_email: '',
    notes: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await clientsAPI.getAll();
      setClients(response.data.data || []);
    } catch (err) {
      console.error('업체 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditMode(true);
      setCurrentClient(client);
      setFormData({
        company_name: client.company_name || '',
        business_number: client.business_number || '',
        address: client.address || '',
        manager_name: client.manager_name || '',
        manager_phone: client.manager_phone || '',
        manager_email: client.manager_email || '',
        notes: client.notes || '',
      });
    } else {
      setEditMode(false);
      setCurrentClient(null);
      setFormData({
        company_name: '',
        business_number: '',
        address: '',
        manager_name: '',
        manager_phone: '',
        manager_email: '',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentClient(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.company_name) {
      alert('업체명은 필수입니다.');
      return;
    }

    try {
      if (editMode) {
        await clientsAPI.update(currentClient.client_id, formData);
      } else {
        await clientsAPI.create(formData);
      }
      handleCloseDialog();
      loadClients();
    } catch (err) {
      alert(err.response?.data?.message || '저장에 실패했습니다.');
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`${client.company_name} 업체를 삭제하시겠습니까?`)) return;
    try {
      await clientsAPI.delete(client.client_id);
      loadClients();
    } catch (err) {
      alert(err.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          업체 관리
        </Typography>
        <Box display="flex" gap={1}>
          <IconButton onClick={loadClients} title="새로고침">
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            업체 등록
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 2, p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          총 {clients.length}개 업체
        </Typography>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>업체명</TableCell>
              <TableCell>담당자</TableCell>
              <TableCell>연락처</TableCell>
              <TableCell>주소</TableCell>
              <TableCell align="center">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  등록된 업체가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.client_id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Business fontSize="small" color="primary" />
                      <Box>
                        <Typography fontWeight={600}>{client.company_name}</Typography>
                        {client.business_number && (
                          <Typography variant="caption" color="textSecondary">
                            {client.business_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{client.manager_name || '-'}</TableCell>
                  <TableCell>
                    {client.manager_phone && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Phone fontSize="small" color="action" />
                        {formatPhone(client.manager_phone)}
                      </Box>
                    )}
                    {client.manager_email && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">{client.manager_email}</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {client.address || '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="수정">
                      <IconButton size="small" onClick={() => handleOpenDialog(client)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                      <IconButton size="small" color="error" onClick={() => handleDelete(client)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 등록/수정 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? '업체 수정' : '업체 등록'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                required
                label="업체명"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="사업자번호"
                name="business_number"
                value={formData.business_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="주소"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="담당자명"
                name="manager_name"
                value={formData.manager_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="담당자 전화"
                name="manager_phone"
                value={formData.manager_phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="담당자 이메일"
                name="manager_email"
                value={formData.manager_email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="메모"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editMode ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientList;
